import { OverlayAlertService, OverlayAlertRef, OverlayAlertConfig } from '@gridscale/ingrid/components/overlay-alert';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgrxService } from './../../../../../../app/@shared/services/ngrx.service';
import { AccountService } from './../../../../../../app/@shared/services/account.service';
import { ToastService } from './../../../../../../app/@shared/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import * as BreakoutActions from './../../../../../../app/@store/breakouts/breakouts.actions';
import * as AccountActions from './../../../../../../app/@store/account/account.actions';
import * as AccountSelectors from './../../../../../../app/@store/account/account.selectors';
import { Store } from '@ngrx/store';
import { selectRouteParam } from './../../../../../../app/@store/router/router.selector';
import { Subject, Subscription } from 'rxjs';
import * as _ from 'lodash';
import { InputTextModule } from '@js/primeng17/inputtext';
import { SharedModule } from '@js/primeng17/api';
import { AutocompleteComponent, AutocompleteSuggestion } from "@gridscale/ingrid/components/autocomplete";
import { FormValidationErrorsComponent } from '@gridscale/ingrid/components/form-validation-errors';

@Component({
    selector: 'app-create-breakout',
    templateUrl: './create-breakout.component.html',
    styleUrls: ['./create-breakout.component.scss'],
    imports: [
        AutocompleteComponent,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        InputTextModule,
        SharedModule,
        AutocompleteComponent,
        FormValidationErrorsComponent,
    ]
})
export class CreateBreakoutComponent implements OnInit, OnDestroy {
  createForm = this.fb.group({
    name: ['', Validators.required],
    project_uuid: ['', Validators.required]
  });
  submitting = false;
  projects?: AutocompleteSuggestion[];

  inAccountMode = false;
  accountUuid?: string;

  private destroyed$: Subject<void> = new Subject();
  protected validationSubscription?: Subscription;

  constructor(
    public ref: OverlayAlertRef,
    private config: OverlayAlertConfig,
    private readonly fb: UntypedFormBuilder,
    private translate: TranslateService,
    private toastService: ToastService,
    private accountService: AccountService,
    private router: Router,
    private route: ActivatedRoute,
    private ngrxService: NgrxService,
    private readonly overlayAlert: OverlayAlertService,
    private readonly store: Store
  ) { }

  ngOnInit(): void {
    this.validationSubscription = this.createForm.statusChanges.subscribe(_status => {
      this.ref.updateButton(1, { disabled: _status !== 'VALID' });
    });

    const location_uuid = this.config.data?.location_uuid;

    this.store.select(selectRouteParam('account_uuid')).pipe(
      takeUntil(this.destroyed$),
      take(1)
    ).subscribe(currentAccountUuid => {
      if (currentAccountUuid !== undefined) {
        this.inAccountMode = true;
        this.accountUuid = currentAccountUuid;

        // we only show projects of contract
        this.store.select(AccountSelectors.isAccountLoaded({ uuid: currentAccountUuid })).pipe(
          withLatestFrom(this.store.select(AccountSelectors.isAccountLoading({ uuid: currentAccountUuid }))),
          take(1)
        ).subscribe(([loaded, loading]) => {
          if (!loaded && !loading) {
            this.store.dispatch(AccountActions.loadAccount({ account_uuid: currentAccountUuid }));
          }
        });

        this.store.select(AccountSelectors.getAccount({ uuid: currentAccountUuid })).pipe(
          takeUntil(this.destroyed$),
          filter(account => account?.relations !== undefined),
          take(1)
        ).subscribe(account => {
          this.projects = _.map(account!.relations!.projects, p => ({ value: p.object_uuid, label: p.name }));
        });


      } else {
        // show all projects grouped by contract
        this.accountService.isAccountProjectsLoaded$.pipe(
          filter(loaded => !!loaded),
          take(1)
        ).subscribe(() => {
          this.projects = this.accountService.getProjectGroupedByAccount(location_uuid);

          // this.projects.forEach(project => {
          //   if (project.children) {
          //     project.children.forEach(childValue => {
          //       childValue.label = project.label;
          //     });
          //   }
          // });
        });
      }
    });


  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    if (this.validationSubscription) {
      this.validationSubscription.unsubscribe();
    }
  }

  onSubmit() {
    if (!this.createForm.invalid) {
      this.submitting = true;
      this.ref.setLoading(true);


      this.ngrxService.callBasicActionWithCallbacks({
        actionToCall: BreakoutActions.createBreakout({
          name: this.createForm.value?.name,
          project_uuid: this.createForm.value?.project_uuid,
        }),
        successAction: BreakoutActions.createBreakoutSuccess,
        failureAction: BreakoutActions.createBreakoutFailure,
        successCallback: action => {
          this.submitting = false;
          this.ref.setLoading(false);
          if (!this.inAccountMode) {
            if (this.inAccountMode) {
              this.router.navigate(['/Accounts', 'SDN', 'Breakouts', action.breakout_uuid], { relativeTo: this.route, queryParams: { account: this.accountUuid } });
            } else {
              this.router.navigate(['/Accounts', 'SDN', 'Breakouts', action.breakout_uuid], { relativeTo: this.route });
            }
          }
          this.ref.close();
          this.toastService.add({
            severity: 'success',
            summary: this.translate.instant('sdn_breakout_create_success')
          });
        },
        failureCallback: () => {
          this.ref.setLoading(false);
        }
      });
    }
  }

  cancel() {
    if (this.submitting === true || this.createForm.dirty) {
      this.overlayAlert.confirm(
        this.translate.instant('DISCARD_CHANGES_HEADLINE'),
        this.translate.instant('DISCARD_CHANGES_INTRO'),
        [
          {
            label: this.translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(false);
            }
          },
          {
            label: this.translate.instant('DISCARD_CHANGES_BTN'),
            mode: 'danger',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(true);
            }
          }
        ],
        'icon-a059_warning').subscribe(_confirm => {
          if (_confirm) {
            this.ref.close();
          } else {
            this.ref.setFocus();
          }
        });
    } else {
      this.ref.close();
    }
  }

}
